import React, { Component } from "react";
import PropTypes from "prop-types";
import styled from "styled-components";
import { RichText } from "prismic-dom";
import { Box, Flex, Image } from "rebass";
import { wordWrap } from "polished";
import {
  Head,
  H1,
  H2,
  H3,
  H4,
  Text,
  Card,
  CardLayout,
  Article,
  Main,
  GradientHeader,
  PageHeader,
  PageSidebar,
  PageContent,
  PageSection,
  CalloutCard,
  CalloutContainer,
  Button,
  UpdateCard,
  PrimaryButton,
} from "components";
import Link from "gatsby-link";

const UpdateLayout = styled(Flex)`
  align-items: stretch;

  @media (max-width: 30em) {
    flex-wrap: wrap;
    > * {
      width: 100%;
      margin-bottom: ${(props) => props.theme.space[1]}px;

      &:not(:lang(ar)) {
        margin-left: 0;
      }
      &:lang(ar) {
        margin-right: 0;
      }
    }
    > *:last-child {
      margin-bottom: ${(props) => props.theme.space[0]}px;
    }
  }
  @media (min-width: 30em) {
    flex-wrap: wrap;
    > * {
      width: ${(props) =>
        "calc(" + (1 / 2) * 100 + "% - " + props.theme.space[4] / 2 + "px)"};
      margin-bottom: ${(props) => props.theme.space[5]}px;

      &:not(:lang(ar)) {
        margin-left: ${(props) => props.theme.space[4]}px;
      }
      &:lang(ar) {
        margin-right: ${(props) => props.theme.space[4]}px;
      }
    }
    > *:nth-child(2n + 1) {
      &:not(:lang(ar)) {
        margin-left: 0;
      }
      &:lang(ar) {
        margin-right: 0;
      }
    }
    > *:nth-last-child(-n + 2) {
      margin-bottom: ${(props) => props.theme.space[0]}px;
    }
  }
`;

const CustomText = styled(Text)`
  ${wordWrap("break-word")};
`;

const Prismic = require("prismic-javascript");

let linkResolver = function (doc) {
  return "/" + doc.type;
};

class Staff extends React.Component {
  static contextTypes = {
    setAltLanguages: PropTypes.func,
  };
  constructor(props) {
    super(props);
    this.state = {
      updates: [],
      updateLimit: 12,
      updatePage: 1,
    };
  }

  componentDidMount() {
    this.context.setAltLanguages(undefined);
    this.getUpdates();
  }

  getUpdates = (add) => {
    let predicates = [
      Prismic.Predicates.at("document.type", "update"),
      Prismic.Predicates.at(
        "my.update.author",
        this.props.data.staff.prismicId
      ),
      Prismic.Predicates.not("my.update.uid", "hidden-update"),
    ];
    let page = 1;
    if (add) {
      page = this.state.updatePage + 1;
    }
    Prismic.getApi("https://bankinformationcenter.cdn.prismic.io/api/v2")
      .then((api) => {
        return api.query(predicates, {
          lang: this.props.data.staff.lang,
          fetchLinks: "region.uid",
          page: page,
          pageSize: this.state.updateLimit,
          orderings: "[my.update.publish_date desc]",
        });
      })
      .then((response) => {
        if (!add) {
          this.setState({ updates: response.results, updatePage: 1 });
        } else {
          let updatesCurrent = JSON.parse(JSON.stringify(this.state.updates));
          let updates = updatesCurrent.concat(response.results);
          this.setState({ updates: updates, updatePage: page });
        }
      });
  };

  render() {
    let json = "";
    if (this.props.data.staff.raw) {
      json = JSON.parse(this.props.data.staff.raw);
    }
    return (
      <div
        style={
          this.props.transition ? this.props.transition.style : { opacity: 0 }
        }
      >
        <Head
          source={{
            meta_title: [
              {
                text:
                  json.data.title !== undefined
                    ? json.data.title[0]
                      ? json.data.title[0].text + " | Bank Information Center"
                      : ""
                    : "",
              },
            ],
            meta_description: [
              {
                text:
                  json.data.description !== undefined
                    ? json.data.description[0]
                      ? json.data.description[0].text
                      : ""
                    : "",
              },
            ],
            social_sharing_image: {
              url:
                json.data.header_image !== undefined
                  ? json.data.header_image.url
                  : "",
            },
          }}
        />
        <Main>
          <PageHeader title={json.data.title[0].text} color={json.type}>
            {json.data.subtitle[0] && (
              <Text children={json.data.subtitle[0].text} />
            )}
          </PageHeader>
          <PageSection>
            <PageSidebar mobileTop="true">
              <Flex
                flexDirection={["column", "row", null, "column"]}
                mt={[-48, null, null, 0]}
                mx={[0, -32, -48, 0]}
              >
                {json.data.header_image.url && (
                  <Box
                    flex="!"
                    width={["initial", 1 / 2, 1 / 1]}
                    mx={[-24, 0]}
                    mb={[0, null, null, 2]}
                  >
                    <Image
                      srcSet={
                        json.data.header_image.quarter.url +
                        " 1000w, " +
                        json.data.header_image.half.url +
                        " 2000w, " +
                        json.data.header_image.url +
                        " 2500w"
                      }
                      w="100%"
                      alt={"Headshot of " + json.data.title[0].text}
                    />
                  </Box>
                )}
                <Box
                  flex="!"
                  width={["initial", 1 / 2, 1 / 1]}
                  mx={[-24, 0]}
                  px={4}
                  py={5}
                  bg="grey.1"
                >
                  {json.data.phone && (
                    <Box mb={4}>
                      <H4
                        children={
                          json.lang === "en-us"
                            ? "Phone"
                            : json.lang === "es-mx"
                            ? "Teléfono"
                            : "الهاتف"
                        }
                        mb={1}
                      />
                      <CustomText children={json.data.phone} />
                    </Box>
                  )}
                  {json.data.email && (
                    <Box>
                      <H4
                        children={
                          json.lang === "en-us"
                            ? "Email"
                            : json.lang === "es-mx"
                            ? "E-mail"
                            : "البريد الإلكتروني"
                        }
                        mb={1}
                      />
                      <CustomText>
                        <a href={"mailto:" + json.data.email}>
                          {json.data.email}
                        </a>
                      </CustomText>
                    </Box>
                  )}
                </Box>
              </Flex>
            </PageSidebar>
            <PageContent>
              <Box>
                {json.data.description && (
                  <Article
                    dangerouslySetInnerHTML={{
                      __html: RichText.asHtml(
                        json.data.description,
                        linkResolver
                      ),
                    }}
                  />
                )}
                <Box mt={4}>
                  <Button
                    children={
                      json.lang === "en-us"
                        ? "Meet the whole team"
                        : json.lang === "es-mx"
                        ? "Conoce a todo el equipo"
                        : "قابل الفريق بأكمله"
                    }
                    is={Link}
                    to={"/" + json.lang + "/team"}
                  />
                </Box>
              </Box>
            </PageContent>
          </PageSection>
          <PageSection borderTop="true" column="true">
            <PageContent>
              <H2
                children={
                  json.lang === "en-us"
                    ? "Updates by " + json.data.title[0].text
                    : json.lang === "es-mx"
                    ? "Actualizaciones de " + json.data.title[0].text
                    : "تحديثات بواسطة " + json.data.title[0].text
                }
                mb={5}
                fontSize={[4, 5]}
              />
              {this.state.updates && (
                <UpdateLayout>
                  {this.state.updates.map((update, index) => (
                    <UpdateCard
                      key={"update" + index}
                      object={update}
                      institutions={this.props.data.institutions.edges}
                      campaigns={this.props.data.campaigns.edges}
                    />
                  ))}
                </UpdateLayout>
              )}
              {this.state.updates.length !==
                this.state.updateLimit * this.state.updatePage && (
                <Flex width="100%" justify="center">
                  <Box pt={6} style={{ textAlign: "center" }}>
                    <Text
                      children={
                        json.lang === "en-us"
                          ? "Didn't find what you are looking for?"
                          : json.lang === "es-mx"
                          ? "¿No encontró lo que estaba buscando?"
                          : "لم تجد ما تبحث عنه؟"
                      }
                      mb={3}
                    />
                    <PrimaryButton
                      children={
                        json.lang === "en-us"
                          ? "Get in touch"
                          : json.lang === "es-mx"
                          ? "Póngase en contacto"
                          : "اتصل بنا"
                      }
                      is={Link}
                      to={"/" + json.lang + "/contact"}
                    />
                  </Box>
                </Flex>
              )}
              {this.state.updates.length ===
                this.state.updateLimit * this.state.updatePage && (
                <Flex width="100%" justify="center">
                  <Box pt={6}>
                    <PrimaryButton
                      children={
                        json.lang === "en-us"
                          ? "View More"
                          : json.lang === "es-mx"
                          ? "Ver más"
                          : "عرض المزيد"
                      }
                      onClick={() => {
                        this.getUpdates(true);
                      }}
                    />
                  </Box>
                </Flex>
              )}
            </PageContent>
          </PageSection>
        </Main>
      </div>
    );
  }
}

export default Staff;

export const query = graphql`
  query staffQuery($id: String!, $lang: String!) {
    staff: prismicDocument(prismicId: { eq: $id }) {
      uid
      prismicId
      id
      raw
      lang
    }
    campaigns: allPrismicDocument(
      filter: { type: { eq: "campaign" }, lang: { eq: $lang } }
    ) {
      edges {
        node {
          uid
          prismicId
          data {
            title {
              text
            }
          }
        }
      }
    }
    institutions: allPrismicDocument(
      filter: { type: { eq: "institution" }, lang: { eq: $lang } }
    ) {
      edges {
        node {
          uid
          prismicId
          data {
            title {
              text
            }
          }
        }
      }
    }
  }
`;



// WEBPACK FOOTER //
// ./src/layouts/staff.js